import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Divider, Container,
  CircularProgress, Button, Grid, TextField, Input } from '@mui/material';
import OrganizationsHeader from "../dashboardnavebar";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import { OrganizationsSubmitproject } from "../../../helper/base_url";

const PostProjectPage = () => {
  const [projectData, setprojectData] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const storedFormData = localStorage.getItem('projectFormData');
    if (storedFormData) {
      setprojectData(JSON.parse(storedFormData));
    }
  }, []);

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    const allowedTypes = ['application/pdf', 'application/zip', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    if (!allowedTypes.includes(uploadedFile.type)) {
      toast.error("Please upload a valid file type (.doc, .pdf, .zip).");
      return;
    }
    if (uploadedFile.size > 10 * 1024 * 1024) {
      toast.error("Max file size is 10MB.");
      return;
    }

    setFile(uploadedFile);
  };

  const handleSubmit = async () => {
    if (!projectData) {
      toast.error("Form data missing.");
      return;
    }
  
    if (Array.isArray(projectData.domain)) {
      projectData.domain = projectData.domain.join(', ');
    } else if (typeof projectData.domain !== 'string') {
      projectData.domain = '';
    }
  
    const formData = new FormData();
    if (file) formData.append("fileInput", file);
  
    formData.append("Projects", JSON.stringify({
      companyName: projectData?.companyName || "",
      websiteOrLinkedIn: projectData?.websiteOrLinkedIn || "",
      companyOverview: projectData?.companyOverview || "",
      projectTitle: projectData?.projectTitle || "",
      preferredTechStack: projectData?.preferredTechStack || "",
      projectSummary: projectData?.projectSummary || "",
      expectedDeliverables: projectData?.expectedDeliverables || "",
      preferredProgrammingLanguages: projectData?.preferredProgrammingLanguages || "",
      domain: projectData?.domain || "",
      projectDuration: projectData?.projectDuration || "",
      weeklyTimeCommitment: projectData?.weeklyTimeCommitment || "",
      modeOfWork: projectData?.modeOfWork || "",
      desiredStartDate: projectData?.desiredStartDate || "",
      applicationDeadline: projectData?.applicationDeadline || "",
      compensation: projectData?.compensation || "",
      confidentialityOrIPClauses: projectData?.confidentialityOrIPClauses || "",
      pointOfContactName: projectData?.pointOfContactName || "",
      pointOfContactDesignation: projectData?.pointOfContactDesignation || "",
      pointOfContactEmail: projectData?.pointOfContactEmail || "",
      pointOfContactPhoneNumber: projectData?.pointOfContactPhoneNumber || "",
    }));
  
    try {
      const token = localStorage.getItem("company_token");
      if (!token) {
        toast.error("Authentication token missing.");
        return;
      }
  
      setLoading(true);
  
      const response = await axios.post(`${OrganizationsSubmitproject}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (response.data.status?.toLowerCase() === "success") {
      toast.success("Project uploaded successfully!");
      localStorage.removeItem('projectFormData');
      setTimeout(() => navigate("/organizations/project-confirmation"), 2000);
    } else {
      toast.error(response.data.message || "Failed to upload project.");
    }
    } catch (error) {
      //console.error("Submission error:", error.response?.data || error.message || error);
      toast.error("Error during project submission. Please try again later.");
    } finally {
      setLoading(false);
    }
  };  

  if (!projectData) {
    return (
      <Container sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
        <Typography variant="h6" mt={2}>
          Loading project data...
        </Typography>
      </Container>
    );
  }

  return (
   <div className="students-flex">
      <OrganizationsHeader />
      <ToastContainer />
      <Box
         sx={{
            background: 'linear-gradient(to right, #e0f7fa, #f1f8e9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            height : '91vh',
            overflowY: 'auto',
            p: { xs: 1.25, sm: 5 },
          }}
                >
          <Card
            sx={{
              width : '100%',
              maxWidth : { xs: '100%', sm: 600, md: 800, lg: 1000 },
              mx: 'auto',
              p: { xs: 2, sm: 4 },
              borderRadius: 4,
              boxShadow: 5,
            }}
          >
         <Typography
            variant="h5"
            fontWeight={600}
            textAlign="center"
            color="primary.main"
            gutterBottom
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            Review Your Project Submission
          </Typography>

          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            mb={4}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            Please verify the details before final submission.
          </Typography>

          {/* Project Info Display */}
          <Grid container spacing={3} mb={4}>
            {[
              { label: "Company Name", value: projectData.companyName },
              { label: "Company Overview", value: projectData.companyOverview },
              { label: "Website / LinkedIn", value: projectData.websiteOrLinkedIn },
              { label: "Project Title", value: projectData.projectTitle },
              { label: "Project Summary", value: projectData.projectSummary },
              { label: "Preferred Tech Stack", value: projectData.preferredTechStack },
              { label: "Programming Languages", value: projectData.preferredProgrammingLanguages },
              { label: "Domain", value: Array.isArray(projectData.domain) ? projectData.domain.join(', ') : projectData.domain },
              ...(projectData.domain?.includes("Other")
                ? [{ label: "Other Domain", value: projectData.otherDomain }]
                : []),
              { label: "Project Duration", value: projectData.projectDuration },
              { label: "Weekly Time Commitment", value: projectData.weeklyTimeCommitment },
              { label: "Work Mode", value: projectData.modeOfWork },
              { label: "Desired Start Date", value: projectData.desiredStartDate },
              { label: "Application Deadline", value: projectData.applicationDeadline },
              { label: "Compensation Type", value: projectData.compensationType },
              ...(projectData.compensationType === "Paid" && projectData.compensation
                ? [{ label: "Compensation Amount", value: `₹${projectData.compensation}` }]
                : []),
              { label: "Confidentiality / IP Clauses", value: projectData.confidentialityOrIPClauses },
              { label: "Contact Name", value: projectData.pointOfContactName },
              { label: "Designation", value: projectData.pointOfContactDesignation },
              { label: "Email", value: projectData.pointOfContactEmail },
              { label: "Phone Number", value: projectData.pointOfContactPhoneNumber },
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
               <TextField
                fullWidth
                label={item.label}
                value={item.value || ""}
                variant="outlined"
                disabled
                multiline={["Company Overview", "Project Summary", "Expected Deliverables", "Preferred Tech Stack", "Programming Languages", "Confidentiality / IP Clauses"].includes(item.label)}
                minRows={["Company Overview", "Project Summary", "Expected Deliverables", "Preferred Tech Stack", "Programming Languages", "Confidentiality / IP Clauses"].includes(item.label) ? 3 : 1}
              />
              </Grid>
            ))}
          </Grid>
          {/* File Upload Section */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Upload Supporting Document
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" mb={2}>
                Accepted file types: .pdf, .doc, .docx, .zip | Max size: 10MB
              </Typography>
              <Input
                type="file"
                inputProps={{ accept: '.pdf, .doc, .docx, .zip' }}
                onChange={handleFileChange}
                fullWidth
              />
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box textAlign="center" gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate('/organizations/post-project')}
              sx={{ px: 4, py: 1.5, mr: 2 }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ px: 4, py: 1.5 }}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit Project"}
            </Button>
          </Box>
        </Card>
      </Box>
    </div>
  );
};

export default PostProjectPage;
